"use client"

import { hasExtendedSlotHours } from "@/lib/branch-hours"
import { useTimeSlots } from "@/hooks/use-time-slots"
import SlotsGrid from "@/components/slots-grid"
import SlotsSkeleton from "@/components/slots-skeleton"
import { AlertCircle, RefreshCcw } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useState, useEffect, useRef } from "react"

const MAX_TRIALS = 2
const RETRY_DELAY_MS = 5000

export default function SlotsGridContainer({
  branchId,
  branchName,
  serviceId,
  appointmentDate,
  calendarType,
}: {
  branchId: string
  branchName?: string
  serviceId: string
  appointmentDate: string
  calendarType?: 'GC' | 'EC'
}) {
  const { data: slots = [], isLoading, error, refetch } = useTimeSlots(branchId, serviceId, appointmentDate)
  const { t } = useTranslation()
  const [retryCount, setRetryCount] = useState(0)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Branch IDs with extended hours until 20:00
  const extendedBranchIds = [
    "90e302ff-3fb1-4da5-9a98-98e7653ed81e", // Ledeta
    "f568edee-7fed-499e-968f-6f237adb0485", // Megenagna
    "863a1ca3-71e4-4c10-8b05-5dfdbfdce39f", // Megenagna VIP
  ]

  // Branch IDs with extended hours until 19:00
  const extendedBranchIds19 = [
    "ec57f424-0266-46ab-ba1f-279694ccbdb0",
    "18b0dc5c-6c42-438b-aa79-ebdc7a8c6495",
    "8f034407-d840-4488-83cd-8e67070cbce6",
    "646d2e72-876f-4385-aa0c-2b02e4a86eef",
    "9aa4e605-f4ea-451a-a389-75cc1ae741f6",
    "a301089b-655c-4223-9b12-e5f58fb58e70",
    "8833e542-05d1-4490-80ce-bea5d1e72547"
  ]

  const cutoffTime = extendedBranchIds.includes(branchId)
    ? "20:00:00"
    : extendedBranchIds19.includes(branchId)
      ? "19:00:00"
      : "17:00:00"
  const filteredSlots = slots ? slots.filter(slot => slot.time_from < cutoffTime) : []
  const hasSlots = filteredSlots.length > 0

  useEffect(() => {
    if (hasSlots) setRetryCount(0)
  }, [hasSlots])

  useEffect(() => {
    if (!branchId || !serviceId || !appointmentDate || isLoading || error || hasSlots) return
    if (retryCount >= MAX_TRIALS - 1) return

    retryTimeoutRef.current = setTimeout(() => {
      refetch().then(() => setRetryCount((c) => c + 1))
    }, RETRY_DELAY_MS)

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [branchId, serviceId, appointmentDate, hasSlots, isLoading, error, retryCount, refetch])

  if (isLoading) return <SlotsSkeleton />

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center animate-fade-in-up">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4 shadow-inner">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">{t("Failed to load slots")}</h3>
        <p className="text-gray-500 text-sm max-w-xs mx-auto mb-6">
          {t("Something went wrong while fetching available times. Please try again.")}
        </p>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-5 py-2 bg-pink-600 text-white rounded-full text-sm font-bold hover:bg-gray-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 active:scale-95"
        >
          <RefreshCcw size={14} />
          {t("Try Again")}
        </button>
      </div>
    )
  }

  if (!hasSlots) {
    if (retryCount < MAX_TRIALS - 1) {
      return <SlotsSkeleton />
    }
  }

  return <SlotsGrid slots={filteredSlots} selectedDate={appointmentDate} calendarType={calendarType} />
}




